import React from "react"
import { useLocation, type Location, type NavigateFunction } from "react-router"
import { useStableNavigate } from "./useStableNavigate"

export type { Location, NavigateFunction }

/**
 * Роутер для классовых компонентов: `navigate` и `location` пропами.
 *
 * Хуки роутера в классах недоступны, а `withRouter` в шестой версии убран —
 * вместо него документация react-router предлагает ровно такую обёртку.
 * Ничего из пятой версии она не воспроизводит: нет ни объекта `history`,
 * ни подписки `listen`. Смена адреса приходит новым `location` в пропах —
 * реагировать на неё надо в `componentDidUpdate`, сравнивая с прежним.
 *
 * `params` нарочно не отдаётся: классам он не нужен (идентификаторы они
 * разбирают из `location.pathname`), а `useParams` возвращает новый объект
 * на каждой перерисовке дерева маршрутов — `PureComponent`-страницы
 * перерисовывались бы вместе с ним.
 *
 * Функциональные компоненты берут то же самое хуками напрямую.
 */
export interface NavigationProps {
    navigate: NavigateFunction
    location: Location
}

export function withNavigation<TProps extends NavigationProps>(
    Component: React.ComponentType<TProps>
): React.FC<Omit<TProps, keyof NavigationProps>> {
    const Wrapped: React.FC<Omit<TProps, keyof NavigationProps>> = (props) => {
        const navigate = useStableNavigate()
        const location = useLocation()

        // приведение нужно одно: TypeScript не выводит, что Omit<TProps, K> & Pick<TProps, K> — это TProps
        return <Component {...({ ...props, navigate, location } as unknown as TProps)} />
    }

    Wrapped.displayName = `withNavigation(${Component.displayName || Component.name || "Component"})`

    return Wrapped
}
